/**
 * CDT Data Table — Divi 5 Visual Builder Component
 *
 * moduleComponent   : preview shown in the VB canvas
 * settingsPanelComponent : full settings panel in the sidebar
 *
 * Attribute updates go via WordPress's core/block-editor store — the same
 * store Divi 5 uses internally to persist block (module) state.
 */

/* global React */

import { registerModule } from '@divi/module-library';

const { useState, useEffect, useCallback, useRef } = React;

// ─── Attribute update helpers ────────────────────────────────────────────────

/**
 * Update a single module attribute using WP's block-editor data store.
 * Divi 5 modules ARE Gutenberg blocks; this is the standard mechanism.
 *
 * @param {string} clientId  The block client ID (from props.id).
 * @param {string} key       Attribute name (camelCase, matching module.json).
 * @param {*}      value     New value.
 */
const setModuleAttr = ( clientId, key, value ) => {
    if ( ! clientId ) return;
    const store = window?.wp?.data?.dispatch( 'core/block-editor' );
    if ( store?.updateBlockAttributes ) {
        store.updateBlockAttributes( clientId, { [ key ]: value } );
    }
};

/**
 * Resolve the currently-selected block's clientId.
 * Uses propId when available (passed as `id` prop to our components),
 * otherwise falls back to WP's selection store.
 */
const resolveClientId = ( propId ) => {
    if ( propId ) return propId;
    const select = window?.wp?.data?.select( 'core/block-editor' );
    return select?.getSelectedBlockClientId?.() ?? null;
};

// ─── REST API helper ─────────────────────────────────────────────────────────

/**
 * Fetch available CDT tables from our REST endpoint.
 * Returns an array of { id, name, slug } objects.
 */
const fetchTables = async () => {
    const apiFetch = window?.wp?.apiFetch;
    if ( ! apiFetch ) return [];
    try {
        const res = await apiFetch( { path: '/cdt/v1/available-tables' } );
        return Array.isArray( res?.tables ) ? res.tables : [];
    } catch ( e ) {
        return [];
    }
};

// ─── Shared inline styles ────────────────────────────────────────────────────

const css = {
    panel: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '13px',
        color: '#1e293b',
        lineHeight: 1.5,
    },
    sectionHead: {
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: '#94a3b8',
        margin: '0',
        padding: '12px 16px 6px',
    },
    divider: { border: 'none', borderTop: '1px solid #e2e8f0', margin: 0 },
    row: { display: 'flex', flexDirection: 'column', gap: 3, padding: '4px 16px' },
    label: { fontSize: '11px', fontWeight: 600, color: '#475569', userSelect: 'none' },
    hint:  { fontSize: '10px', color: '#94a3b8', fontWeight: 400 },
    input: {
        width: '100%', padding: '5px 8px', border: '1px solid #cbd5e1',
        borderRadius: '4px', fontSize: '12px', background: '#fff', color: '#1e293b',
        boxSizing: 'border-box', outline: 'none',
    },
    select: {
        width: '100%', padding: '5px 8px', border: '1px solid #cbd5e1',
        borderRadius: '4px', fontSize: '12px', background: '#fff', color: '#1e293b',
        boxSizing: 'border-box', cursor: 'pointer', outline: 'none',
    },
    toggleRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 16px',
    },
    toggleLabel: { fontSize: '12px', color: '#374151' },
    colorRow: {
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '3px 16px',
    },
};

// ─── Reusable field components ───────────────────────────────────────────────

const Toggle = ( { label, value, onChange } ) => {
    const on = value === 'on';
    return (
        <div style={ css.toggleRow }>
            <span style={ css.toggleLabel }>{ label }</span>
            <button
                onClick={ () => onChange( on ? 'off' : 'on' ) }
                aria-pressed={ on }
                style={ {
                    position: 'relative', width: 36, height: 20, borderRadius: 999,
                    border: 'none', cursor: 'pointer', flexShrink: 0,
                    background: on ? '#2563eb' : '#cbd5e1',
                    transition: 'background 0.18s',
                    padding: 0,
                } }
            >
                <span style={ {
                    position: 'absolute', top: 2,
                    left: on ? 18 : 2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', transition: 'left 0.18s',
                    boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                } } />
            </button>
        </div>
    );
};

const ColorField = ( { label, value, onChange } ) => {
    const displayVal = value || '';
    return (
        <div style={ css.colorRow }>
            <span style={ { ...css.toggleLabel, flex: 1 } }>{ label }</span>
            <input
                type="color"
                value={ /^#[0-9a-fA-F]{6}$/.test( displayVal ) ? displayVal : '#2563eb' }
                onChange={ e => onChange( e.target.value ) }
                style={ {
                    width: 28, height: 22, border: '1px solid #cbd5e1',
                    borderRadius: 4, padding: 1, cursor: 'pointer',
                    background: 'none', flexShrink: 0,
                } }
                title="Pick colour"
            />
            <input
                type="text"
                value={ displayVal }
                placeholder="#000000 or var(--token)"
                onChange={ e => onChange( e.target.value ) }
                style={ { ...css.input, width: 130, flex: 'none' } }
            />
        </div>
    );
};

// ─── Settings Panel ──────────────────────────────────────────────────────────

const CdtSettingsPanel = ( props ) => {
    const { attrs = {}, id } = props;
    const [ tables, setTables ] = useState( [] );
    const mounted = useRef( true );

    useEffect( () => {
        mounted.current = true;
        fetchTables().then( list => { if ( mounted.current ) setTables( list ); } );
        return () => { mounted.current = false; };
    }, [] );

    const set = useCallback( ( key, value ) => {
        setModuleAttr( resolveClientId( id ), key, value );
    }, [ id ] );

    const v = ( key, def = '' ) => attrs[ key ] ?? def;

    return (
        <div style={ css.panel }>

            {/* ── Table ───────────────────────────────────────────────── */}
            <p style={ css.sectionHead }>Table Settings</p>

            <div style={ css.row }>
                <label style={ css.label }>Data Table</label>
                <select
                    style={ css.select }
                    value={ v( 'tableId' ) }
                    onChange={ e => set( 'tableId', e.target.value ) }
                >
                    <option value="">— Select a table —</option>
                    { tables.map( t => (
                        <option key={ t.id } value={ String( t.id ) }>
                            { t.name }
                        </option>
                    ) ) }
                </select>
            </div>

            <div style={ css.row }>
                <label style={ css.label }>Table Title <span style={ css.hint }>(leave blank to use table name)</span></label>
                <input
                    style={ css.input }
                    type="text"
                    placeholder="My Table"
                    value={ v( 'tableTitle' ) }
                    onChange={ e => set( 'tableTitle', e.target.value ) }
                />
            </div>

            <hr style={ css.divider } />

            {/* ── Pagination ──────────────────────────────────────────── */}
            <p style={ css.sectionHead }>Pagination</p>

            <div style={ css.row }>
                <label style={ css.label }>Mode</label>
                <select
                    style={ css.select }
                    value={ v( 'paginationMode', 'pagination' ) }
                    onChange={ e => set( 'paginationMode', e.target.value ) }
                >
                    <option value="pagination">Numbered pages</option>
                    <option value="load_more">Load more button</option>
                    <option value="infinite">Infinite scroll</option>
                </select>
            </div>

            <div style={ css.row }>
                <label style={ css.label }>Rows per page</label>
                <input
                    style={ css.input }
                    type="number"
                    min="1" max="500"
                    value={ v( 'rowsPerPage', '10' ) }
                    onChange={ e => set( 'rowsPerPage', e.target.value ) }
                />
            </div>

            <hr style={ css.divider } />

            {/* ── Features ────────────────────────────────────────────── */}
            <p style={ css.sectionHead }>Features &amp; Display</p>

            { [
                [ 'Export / Download',    'enableExport',           'on'  ],
                [ 'Column Filters',       'enableColumnFilters',     'on'  ],
                [ 'Column Visibility',    'enableColumnVisibility',  'on'  ],
                [ 'Bulk Actions',         'enableBulkActions',       'off' ],
                [ 'Inline Editing',       'enableInlineEditing',     'off' ],
                [ 'State Persistence',    'enablePersistence',       'on'  ],
            ].map( ( [ label, key, def ] ) => (
                <Toggle
                    key={ key }
                    label={ label }
                    value={ v( key, def ) }
                    onChange={ val => set( key, val ) }
                />
            ) ) }

            <hr style={ css.divider } />

            {/* ── Table Design ────────────────────────────────────────── */}
            <p style={ css.sectionHead }>Table Design</p>

            { [
                [ 'Accent Color',         'designPrimaryColor' ],
                [ 'Accent Hover',         'designPrimaryHover' ],
                [ 'Body Text',            'designTextColor'    ],
                [ 'Title Color',          'designTitleColor'   ],
                [ 'Container BG',         'designBgColor'      ],
                [ 'Header / Alt-Row BG',  'designHeaderBg'     ],
                [ 'Border',               'designBorderColor'  ],
                [ 'Search / Filter BG',   'designInputBg'      ],
            ].map( ( [ label, key ] ) => (
                <ColorField
                    key={ key }
                    label={ label }
                    value={ v( key ) }
                    onChange={ val => set( key, val ) }
                />
            ) ) }

            <div style={ css.row }>
                <label style={ css.label }>
                    Title Font Size <span style={ css.hint }>e.g. 1.5rem · 24px · 2em</span>
                </label>
                <input
                    style={ css.input }
                    type="text"
                    placeholder="1.5rem"
                    value={ v( 'designTitleSize' ) }
                    onChange={ e => set( 'designTitleSize', e.target.value ) }
                />
            </div>

            <div style={ css.row }>
                <label style={ css.label }>Title Font Weight</label>
                <select
                    style={ css.select }
                    value={ v( 'designTitleWeight' ) }
                    onChange={ e => set( 'designTitleWeight', e.target.value ) }
                >
                    <option value="">Default (700)</option>
                    <option value="400">Normal (400)</option>
                    <option value="500">Medium (500)</option>
                    <option value="600">Semi-Bold (600)</option>
                    <option value="700">Bold (700)</option>
                    <option value="800">Extra Bold (800)</option>
                    <option value="900">Black (900)</option>
                </select>
            </div>

            <div style={ css.row }>
                <label style={ css.label }>
                    Border Radius <span style={ css.hint }>e.g. 8px · 0.5rem</span>
                </label>
                <input
                    style={ css.input }
                    type="text"
                    placeholder="8px"
                    value={ v( 'designRadius' ) }
                    onChange={ e => set( 'designRadius', e.target.value ) }
                />
            </div>

            <div style={ { height: 16 } } />
        </div>
    );
};

// ─── Canvas Preview ──────────────────────────────────────────────────────────

const TablePreview = ( { attrs } ) => {
    const tableId    = attrs?.tableId    ?? '';
    const tableTitle = attrs?.tableTitle ?? '';
    const mode       = attrs?.paginationMode ?? 'pagination';
    const rpp        = attrs?.rowsPerPage ?? '10';

    const chips = [
        { k: 'enableExport',           label: '⬇ Export',    bg: '#dcfce7', fg: '#15803d' },
        { k: 'enableColumnFilters',    label: '⚡ Filters',   bg: '#fef3c7', fg: '#92400e' },
        { k: 'enableColumnVisibility', label: '👁 Columns',   bg: '#ede9fe', fg: '#5b21b6' },
        { k: 'enableBulkActions',      label: '☑ Bulk',      bg: '#fef9c3', fg: '#854d0e' },
        { k: 'enableInlineEditing',    label: '✏️ Edit',      bg: '#fce7f3', fg: '#9d174d' },
        { k: 'enableCharts',           label: '📈 Charts',    bg: '#e0f2fe', fg: '#0369a1' },
    ].filter( c => ( attrs?.[ c.k ] ?? 'off' ) === 'on' );

    const paginationLabel =
        mode === 'infinite'  ? '∞ Infinite scroll' :
        mode === 'load_more' ? '＋ Load more button' :
        `📄 Paginated · ${ rpp } rows / page`;

    if ( ! tableId ) {
        return (
            <div style={ {
                padding: '2rem', textAlign: 'center', borderRadius: 10,
                background: 'linear-gradient(135deg,#667eea,#764ba2)',
                color: '#fff', fontFamily: '-apple-system,sans-serif',
            } }>
                <div style={ { fontSize: '2.5rem', marginBottom: 8 } }>📊</div>
                <strong style={ { fontSize: '1.1rem' } }>Dynamic Data Table</strong>
                <p style={ { margin: '6px 0 0', opacity: .8, fontSize: '.85rem' } }>
                    Open the settings panel → select a Data Table to get started.
                </p>
            </div>
        );
    }

    return (
        <div style={ {
            padding: '1.25rem', borderRadius: 10,
            background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
            border: '2px solid #2563eb',
            fontFamily: '-apple-system,sans-serif',
        } }>
            <div style={ { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 } }>
                <span style={ { fontSize: '1.4rem' } }>📊</span>
                <strong style={ { color: '#1e293b', fontSize: '1.05rem', flex: 1 } }>
                    { tableTitle || 'Dynamic Data Table' }
                </strong>
                <span style={ {
                    background: '#2563eb', color: '#fff',
                    fontSize: '.68rem', fontWeight: 700,
                    padding: '2px 8px', borderRadius: 999,
                    textTransform: 'uppercase', letterSpacing: '.05em',
                } }>
                    Table #{ tableId }
                </span>
            </div>

            { chips.length > 0 && (
                <div style={ { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 } }>
                    { chips.map( c => (
                        <span key={ c.k } style={ {
                            background: c.bg, color: c.fg,
                            fontSize: '.7rem', fontWeight: 600,
                            padding: '2px 7px', borderRadius: 4,
                        } }>{ c.label }</span>
                    ) ) }
                </div>
            ) }

            <p style={ { margin: 0, fontSize: '.78rem', color: '#64748b' } }>
                { paginationLabel }
            </p>
        </div>
    );
};

// ─── Module component (canvas) ───────────────────────────────────────────────

const CdtDataTableEdit = ( props ) => {
    const { attrs, id, name, moduleContainer: ModuleContainer, elements } = props;
    return (
        <ModuleContainer attrs={ attrs } elements={ elements } id={ id } name={ name }>
            <TablePreview attrs={ attrs } />
        </ModuleContainer>
    );
};

// ─── Export ──────────────────────────────────────────────────────────────────

export const cdtDataTableModule = {
    name: 'cdt/data-table',
    moduleComponent:      CdtDataTableEdit,
    settingsPanelComponent: CdtSettingsPanel,
};
